/** Реестр функций подписки (ТЗ 03 §3). Синхронизируется в таблицу `features` при сидировании/деплое. */
export const MODULE_FEATURES = [
  "core.pos", "core.menu", "core.tables", "core.kds", "core.inventory_basic", "core.recipes", "core.customers", "core.debts",
  "core.staff", "core.payroll_basic", "core.analytics_basic", "core.qr_menu", "core.website",
  "addon.qr_ordering", "addon.online_payments", "addon.inventory_pro", "addon.procurement", "addon.production", "addon.payroll_pro",
  "addon.finance", "addon.loyalty", "addon.promotions_pro", "addon.reservations", "addon.banquets", "addon.services", "addon.delivery",
  "addon.aggregators", "addon.telegram", "addon.sms", "addon.analytics_pro", "addon.fraud", "addon.reviews", "addon.custom_domain",
  "addon.api_access", "addon.offline_mode", "addon.desktop", "addon.fiscal", "addon.accounting_export", "addon.ai", "addon.white_label",
  "addon.priority_support",
] as const;
export const LIMIT_FEATURES = [
  "limit.branches", "limit.terminals", "limit.users", "limit.warehouses", "limit.menu_items", "limit.storage_gb", "limit.report_history_days", "limit.api_rps",
] as const;
export const QUOTA_FEATURES = ["quota.sms", "quota.online_orders", "quota.ai_requests"] as const;

/** Понятные названия функций и лимитов (Platform Admin, названия дополнений в каталоге). */
export const FEATURE_NAMES: Record<string, string> = {
  "core.pos": "Касса и заказы", "core.menu": "Меню", "core.tables": "Зал и столы", "core.kds": "Экран кухни",
  "core.inventory_basic": "Склад (базовый)", "core.recipes": "Техкарты и себестоимость", "core.customers": "Клиенты",
  "core.debts": "Долги клиентов", "core.staff": "Сотрудники и смены", "core.payroll_basic": "Зарплата (базовая)",
  "core.analytics_basic": "Отчёты (базовые)", "core.qr_menu": "QR-меню", "core.website": "Сайт заведения",
  "addon.qr_ordering": "Заказ по QR-коду", "addon.online_payments": "Онлайн-оплата", "addon.inventory_pro": "Склад (расширенный)",
  "addon.procurement": "Закупки и заявки", "addon.production": "Производство", "addon.payroll_pro": "Зарплата (расширенная)",
  "addon.finance": "Финансы", "addon.loyalty": "Бонусы и скидки гостям", "addon.promotions_pro": "Акции (расширенные)",
  "addon.reservations": "Бронирование столов", "addon.banquets": "Банкеты", "addon.services": "Услуги и аренда",
  "addon.delivery": "Доставка", "addon.aggregators": "Сервисы доставки (Glovo и др.)", "addon.telegram": "Telegram-бот",
  "addon.sms": "SMS-рассылки", "addon.analytics_pro": "Отчёты (расширенные)", "addon.fraud": "Защита от злоупотреблений",
  "addon.reviews": "Отзывы гостей", "addon.custom_domain": "Свой адрес сайта", "addon.api_access": "Подключение внешних программ",
  "addon.offline_mode": "Работа без интернета", "addon.desktop": "Программа кассы для компьютера", "addon.fiscal": "Фискальные чеки",
  "addon.accounting_export": "Выгрузка в 1С", "addon.ai": "Умный помощник", "addon.white_label": "Свой бренд вместо Coca",
  "addon.priority_support": "Быстрая поддержка",
  "limit.branches": "Филиалы", "limit.terminals": "Кассы и терминалы", "limit.users": "Пользователи с паролем",
  "limit.warehouses": "Склады", "limit.menu_items": "Позиции меню", "limit.storage_gb": "Место для файлов, ГБ",
  "limit.report_history_days": "Отчёты за сколько дней", "limit.api_rps": "Запросов внешних программ в секунду",
  "quota.sms": "SMS в месяц", "quota.online_orders": "Онлайн-заказов в месяц", "quota.ai_requests": "Запросов к помощнику в месяц",
};
export type ModuleFeature = (typeof MODULE_FEATURES)[number];

/**
 * Дополнения, которые уже работают. Остальные сид скрывает из каталога клиента (`addons.is_public = false`) — их нельзя купить
 * из ERP, платформа может выдать вручную. Сделали дополнение → добавить сюда и включить «Видно клиентам» в Platform Admin.
 */
export const READY_ADDONS: readonly ModuleFeature[] = [
  "addon.qr_ordering", "addon.reservations", "addon.telegram", "addon.procurement", "addon.finance", "addon.payroll_pro",
  "addon.desktop", "addon.priority_support", "addon.custom_domain", "addon.white_label", "addon.analytics_pro", "addon.fraud", "addon.sms", "addon.services", "addon.banquets", "addon.promotions_pro",
  "addon.delivery", "addon.inventory_pro", "addon.production",
];
export type LimitFeature = (typeof LIMIT_FEATURES)[number];
export type QuotaFeature = (typeof QUOTA_FEATURES)[number];
export type FeatureCode = ModuleFeature | LimitFeature | QuotaFeature;

export const SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due", "read_only", "suspended", "cancelled", "expired", "archived"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/** Лимит null = без ограничений. */
export type Entitlements = {
  version: number;
  status: SubscriptionStatus;
  planCode: string;
  features: ModuleFeature[];
  /** Лимиты и месячные квоты (`quota.*` — из тарифа и пакетов) */
  limits: Partial<Record<LimitFeature | QuotaFeature, number | null>>;
  trialEndsAt: string | null;
  /** Часовой пояс компании — «сегодня» для сроков долгов и оплат поставщикам */
  timezone?: string;
};

/** Статусы, в которых разрешены изменения справочников/настроек. POS-продажи разрешены и в read_only (BR-SaaS-05). */
export const WRITABLE_STATUSES: readonly SubscriptionStatus[] = ["trialing", "active", "past_due", "cancelled"];
export const POS_ALLOWED_STATUSES: readonly SubscriptionStatus[] = [...WRITABLE_STATUSES, "read_only"];

const CORE: ModuleFeature[] = MODULE_FEATURES.filter((f) => f.startsWith("core.") && f !== "core.debts");

/** Состав тарифов по умолчанию (ТЗ 03 §4). Цены задаются в Platform Admin. */
/**
 * Запросов к Coca AI в месяц (`quota.ai_requests`): вопрос в чате или новая подсказка = 1 запрос, ответ из кэша — бесплатно.
 * Больше — пакет `pack.ai500`. null — без ограничений.
 */
export const AI_QUOTA_DEFAULTS: Record<string, number | null> = { start: 100, business: 300, pro: 1000, enterprise: null };

export const DEFAULT_PLANS: { code: string; name: string; features: ModuleFeature[]; limits: Partial<Record<LimitFeature | QuotaFeature, number | null>>; trialDays: number }[] = [
  { code: "start", name: "Start", features: CORE, trialDays: 14, limits: { "limit.branches": 1, "limit.terminals": 2, "limit.users": 5, "limit.warehouses": 1, "limit.report_history_days": 90, "quota.ai_requests": AI_QUOTA_DEFAULTS.start! } },
  { code: "business", name: "Business", features: [...CORE, "core.debts"], trialDays: 14, limits: { "limit.branches": 1, "limit.terminals": 5, "limit.users": 25, "limit.warehouses": 2, "limit.report_history_days": 365, "quota.ai_requests": AI_QUOTA_DEFAULTS.business! } },
  {
    code: "pro",
    name: "Pro",
    trialDays: 14,
    features: [...CORE, "core.debts", "addon.qr_ordering", "addon.inventory_pro", "addon.procurement", "addon.payroll_pro", "addon.finance", "addon.analytics_pro", "addon.telegram", "addon.reservations", "addon.desktop"],
    limits: { "limit.branches": 3, "limit.terminals": 15, "limit.users": 100, "limit.warehouses": 10, "limit.report_history_days": null, "quota.ai_requests": AI_QUOTA_DEFAULTS.pro! },
  },
  { code: "enterprise", name: "Enterprise", features: [...MODULE_FEATURES], trialDays: 0, limits: { "limit.branches": null, "limit.terminals": null, "limit.users": null, "limit.warehouses": null, "limit.report_history_days": null, "quota.ai_requests": null } },
];
